'use client';

import { Box, Container, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { LazyImage } from '../../ui/LazyImage';

interface HighlightItem {
  title: string;
  description: string;
  image?: string;
  imageOnLeft?: boolean;
}

interface FeatureHighlightBlockProps {
  items: HighlightItem[];
}

export function FeatureHighlightBlock({ items }: FeatureHighlightBlockProps) {
  if (!items.length) return null;

  return (
    <Container size="xl" py={{ base: 52, md: 82 }}>
      <Stack gap={24}>
        {items.map((item, index) => (
          <Paper
            key={`${item.title}-${index}`}
            p={{ base: 20, md: 40 }}
            radius={40}
            withBorder
            style={{ background: index % 2 === 0 ? 'var(--surface-base)' : 'var(--surface-muted)', borderColor: 'var(--surface-border)' }}
          >
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing={{ base: 24, md: 36 }}>
              <Stack justify="center" gap={12} style={{ order: item.imageOnLeft ? 2 : 1 }}>
                <Text fw={500} size="sm" tt="uppercase" style={{ color: 'var(--text-muted)', letterSpacing: '0.04em' }}>
                  {`0${index + 1}`}
                </Text>
                <Title order={2} style={{ color: 'var(--text-primary)', fontWeight: 500, lineHeight: 1.3, fontSize: 'clamp(1.8rem, 3.2vw, 2.5rem)' }}>
                  {item.title}
                </Title>
                <Text style={{ color: 'var(--text-secondary)', lineHeight: 1.75 }}>{item.description}</Text>
              </Stack>

              <Paper
                radius={40}
                p={0}
                withBorder
                style={{
                  order: item.imageOnLeft ? 1 : 2,
                  overflow: 'hidden',
                  minHeight: 310,
                  borderColor: 'var(--surface-border)',
                  background: 'var(--surface-soft)',
                }}
              >
                {item.image ? (
                  <LazyImage src={item.image} alt={item.title} h="100%" w="100%" fit="cover" />
                ) : (
                  <Box
                    h="100%"
                    style={{
                      background: 'var(--surface-soft)',
                    }}
                  />
                )}
              </Paper>
            </SimpleGrid>
          </Paper>
        ))}
      </Stack>
    </Container>
  );
}
